import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, ShieldCheck, RefreshCw, FileText, Mail, Phone, Smartphone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ValidateConsentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('loading'); // loading, review, confirming, success, error
  const [consentDetails, setConsentDetails] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const hasLoaded = useRef(false);
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (hasLoaded.current) return;
    
    if (token) {
      hasLoaded.current = true;
      loadConsentDetails();
    } else {
      setStep('error');
      setError('Token não fornecido');
    }
  }, [token]);

  const loadConsentDetails = async () => {
    setStep('loading');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/qr/pending/${token}`);
      setConsentDetails(response.data);
      setStep('review');
    } catch (error) {
      console.error('Error loading consent details:', error);
      setStep('error');
      setError(error.response?.data?.detail || 'QR Code inválido ou expirado');
    }
  };

  const handleConfirmConsent = async () => {
    setStep('confirming');
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/qr/confirm`,
        null,
        { params: { token } }
      );
      
      setResult({
        success: true,
        message: response.data.message,
        consentId: response.data.consent_id,
        purposes: response.data.purposes,
        contactValue: response.data.contact_value
      });
      setStep('success');
      toast.success('Consentimento confirmado!');
    } catch (error) {
      console.error('Confirmation error:', error);
      setStep('error');
      setError(error.response?.data?.detail || 'Erro ao confirmar consentimento');
      toast.error('Confirmação falhou');
    }
  };

  const handleRetry = () => {
    hasLoaded.current = false;
    setError(null);
    setResult(null);
    loadConsentDetails();
    hasLoaded.current = true;
  };

  const getContactIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'phone': return <Phone className="w-4 h-4" />;
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      default: return <Mail className="w-4 h-4" />;
    }
  };

  const getContactLabel = (type) => {
    switch (type) {
      case 'email': return 'Email';
      case 'phone': return 'Telefone';
      case 'mobile': return 'Telemóvel';
      default: return 'Contacto';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-200" data-testid="validate-consent-page">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-[#0F172A] rounded-full flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {step === 'review' ? 'Confirmação de Consentimento' : 'Validação de Consentimento'}
          </CardTitle>
          <CardDescription>
            Privaxia - RGPD
          </CardDescription>
        </CardHeader>
        
        <CardContent className="flex flex-col items-center justify-center py-6">
          {/* Loading State */}
          {step === 'loading' && (
            <div className="text-center space-y-4">
              <RefreshCw className="w-16 h-16 text-[#10B981] mx-auto animate-spin" />
              <p className="text-slate-600">A carregar detalhes do consentimento...</p>
            </div>
          )}

          {/* Review State - Show purposes before confirmation */}
          {step === 'review' && consentDetails && (
            <div className="w-full space-y-5">
              {/* User greeting */}
              {consentDetails.user_name && (
                <div className="text-center">
                  <p className="text-slate-600">Olá, <span className="font-semibold">{consentDetails.user_name}</span></p>
                </div>
              )}

              {/* Contact info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-slate-700">
                  {getContactIcon(consentDetails.contact_type)}
                  <span className="text-sm font-medium">{getContactLabel(consentDetails.contact_type)}:</span>
                  <span className="text-sm">{consentDetails.contact_value}</span>
                </div>
              </div>

              {/* Purposes Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#10B981]" />
                  <h3 className="font-semibold text-slate-800">Finalidades do Consentimento</h3>
                </div>
                <p className="text-sm text-slate-600">
                  Ao confirmar, está a dar o seu consentimento para as seguintes finalidades:
                </p>
                <div className="space-y-2">
                  {consentDetails.purposes.map((purpose, index) => (
                    <div 
                      key={purpose.purpose_id || index}
                      className="bg-emerald-50 border border-emerald-200 rounded-lg p-3"
                    >
                      <p className="font-medium text-emerald-800">{purpose.name}</p>
                      {purpose.description && (
                        <p className="text-sm text-emerald-600 mt-1">{purpose.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Legal notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  <strong>Aviso Legal:</strong> Pode revogar o seu consentimento a qualquer momento contactando-nos ou através do centro de preferências.
                </p>
              </div>

              {/* Confirm Button */}
              <Button
                onClick={handleConfirmConsent}
                className="w-full bg-[#10B981] hover:bg-[#10B981]/90 text-white py-6 text-lg font-semibold"
                data-testid="confirm-consent-btn"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Confirmar Consentimento
              </Button>

              <p className="text-xs text-center text-slate-500">
                Ao clicar em "Confirmar Consentimento", aceita os termos acima indicados.
              </p>
            </div>
          )}

          {/* Confirming State */}
          {step === 'confirming' && (
            <div className="text-center space-y-4">
              <RefreshCw className="w-16 h-16 text-[#10B981] mx-auto animate-spin" />
              <p className="text-slate-600">A confirmar o seu consentimento...</p>
            </div>
          )}

          {/* Success State */}
          {step === 'success' && result && (
            <div className="text-center space-y-4 w-full">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-emerald-600">{result.message}</h3>
                <p className="text-sm text-slate-600 mt-2">
                  O seu consentimento foi registado com sucesso.
                </p>
              </div>
              
              {/* Show confirmed purposes */}
              {result.purposes && result.purposes.length > 0 && (
                <div className="bg-emerald-50 rounded-lg p-4 text-left">
                  <p className="text-sm font-medium text-emerald-800 mb-2">Finalidades confirmadas:</p>
                  <div className="flex flex-wrap gap-2">
                    {result.purposes.map((purpose, index) => (
                      <Badge 
                        key={purpose.purpose_id || index}
                        className="bg-emerald-100 text-emerald-700 border-emerald-200"
                      >
                        {purpose.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {result.contactValue && (
                <p className="text-sm text-slate-600">
                  Contacto: <span className="font-medium">{result.contactValue}</span>
                </p>
              )}
              
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                Consentimento Confirmado
              </Badge>
              
              {result.consentId && (
                <p className="text-xs text-slate-500 mt-4">
                  ID: {result.consentId}
                </p>
              )}
              
              <div className="pt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/login-local')}
                  className="w-full"
                >
                  Ir para Login
                </Button>
              </div>
            </div>
          )}

          {/* Error State */}
          {step === 'error' && (
            <div className="text-center space-y-4 w-full">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-red-600">{error}</h3>
                <p className="text-sm text-slate-600 mt-2">
                  O QR Code pode estar expirado ou já foi utilizado.
                </p>
              </div>
              <Badge className="bg-red-50 text-red-700 border-red-200">
                Validação Falhou
              </Badge>
              
              <div className="pt-6 space-y-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/login-local')}
                  className="w-full"
                >
                  Ir para Login
                </Button>
                <Button
                  onClick={handleRetry}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Tentar Novamente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      <div className="fixed bottom-4 text-center w-full">
        <p className="text-xs text-slate-500">
          © 2026 Privaxia - Conforme RGPD
        </p>
      </div>
    </div>
  );
};

export default ValidateConsentPage;
